import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View } from "react-native";
import useSections from "../../Hooks/useSections";
import useWorkspaces from "../../Hooks/useWorkspaces";
import DictionariesList from "./Components/Dictionary/DictionariesList";
import EntitiesList from "./Components/EntitiesList";
import FunctionsList from "./Components/Functions/FunctionsList";
import ConnectionsList from "./Components/Connections/ConnectionsList";
import AuthorisationsList from "./Components/Authorisations/AuthorisationsList";
import GeneratorConfigList from "./Components/Generation/GeneratorConfigList";
import ParsingSettings from "./Components/ParsingSettings";
import QaConfigList from "./Components/QA/QaConfigList";
import SettingsAI from "./Components/SettingsAI";
import TrainingSettings from "./Components/TrainingSettings";
import SitesList from "./Components/Sites/SitesList";
import { isMobileScreen } from "../MainViewContainer";
import HostingsList from "./Components/Hostings/HostingList";
import ExecEnvsList from "./Components/Execution Environments/ExecEnvsList";
import ChannelsList from "./Components/Channels/ChannelsList";
import BenchmarksList from "./Components/Benchmarks/BenchmarksList";
import TopicsList from "./Components/Topics/TopicsList";
import NERsList from "./Components/NERs/NERsList";
import JobsList from "./Components/Jobs/JobsList";
import TriggersList from "./Components/Triggers/TriggersList";
import AlertsList from "./Components/Alerts/AlertsList";
import AppJoyRide from "../../Components/Joyride";
import useOnboardingTour from "../../Hooks/useOnboardingTour";

const SettingsScreen = ({ workspace, handleWorkspaceUpdate }) => {
  const { t, i18n } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const {
    setDisplayedView,
    displayedView,
    displayedOpt,
    viewRenderer,
  } = useSections();
  const styles = styleGenerator(currentTheme, isMobileScreen());
  const { getWorkspaceBaseModel } = useWorkspaces();
  const { state } = AppStateManager.useAppStateManager();
  const { isPlatformSupported, needsSectionIntro, markSectionIntroSeen } =
    useOnboardingTour();

  const shownIntrosRef = useRef(new Set());
  const [activeIntro, setActiveIntro] = useState(null);
  const previousDisplayRef = useRef(displayedView);
  useEffect(() => {
    if (displayedView && displayedView !== previousDisplayRef.current) {
      previousDisplayRef.current = displayedView;
      const introKey = `settings.joyride.${displayedView}`;
      if (
        isPlatformSupported &&
        i18n.exists(introKey) &&
        !shownIntrosRef.current.has(displayedView) &&
        needsSectionIntro(displayedView)
      ) {
        shownIntrosRef.current.add(displayedView);
        setActiveIntro({ uid: displayedView, key: introKey });
      }
    } else if (!displayedView) {
      setActiveIntro(null);
    }
  }, [displayedView, isPlatformSupported, i18n, needsSectionIntro]);

  const hasActiveIntro = Boolean(activeIntro);
  const sectionIntroSelector = activeIntro
    ? `#settings_section_${activeIntro.uid}`
    : null;

  const sectionIntroSteps = hasActiveIntro
    ? [
        {
          target: sectionIntroSelector,
          content: t(activeIntro.key),
          placement: "bottom",
          disableBeacon: true,
        },
      ]
    : [];

  const mapDisplayedViewWithComponent = (uid) => {
    const Intents =
      state?.aiEntitiesConfig?.filter((i) => i.type === "intent") || [];
    const NERs = state?.aiEntitiesConfig?.filter((i) => i.type === "ner") || [];
    const Topics =
      state?.aiEntitiesConfig?.filter((i) => i.type === "topic") || [];
    Intents.sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
    );
    NERs.sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
    );
    Topics.sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
    );
    switch (uid) {
      case "INTENT":
        return (
          <EntitiesList
            showEmoji={false}
            entities={Intents}
            buttonTitle={t("settings.list.createIntent")}
            isCreateEnabled={getWorkspaceBaseModel() === "none"}
            createEntitityUID={"INTENT_NEW"}
            editEntityUID={"INTENT_EDIT"}
            settingType="intent"
          />
        );
      case "INTENT_NEW":
        return (
          <SettingsAI
            backTo="INTENT"
            settingType="intent"
            isEdit={false}
            setDisplayedView={setDisplayedView}
            opt={displayedOpt}
            sectionTitle={t("settings.list.new")}
          />
        );
      case "INTENT_EDIT":
        return (
          <SettingsAI
            backTo="INTENT"
            isEdit
            sectionTitle={displayedOpt?.label}
            settingType="intent"
            opt={displayedOpt}
            initialValues={{ ...displayedOpt }}
            setDisplayedView={setDisplayedView}
          />
        );
      case "NER":
        return (
          <NERsList
            buttonTitle={t("settings.list.createNer")}
            createEntitityUID={"NER_NEW"}
            editEntityUID={"NER_EDIT"}
            ners={NERs}
          />
        );
      case "NER_NEW":
        return (
          <SettingsAI
            backTo="NER"
            settingType="ner"
            isEdit={false}
            setDisplayedView={setDisplayedView}
            opt={displayedOpt}
            sectionTitle={t("settings.list.new")}
          />
        );
      case "NER_EDIT":
        return (
          <SettingsAI
            backTo="NER"
            settingType="ner"
            isEdit
            sectionTitle={displayedOpt?.label}
            opt={displayedOpt}
            initialValues={{ ...displayedOpt }}
            setDisplayedView={setDisplayedView}
          />
        );

      case "TOPIC":
        return (
          <TopicsList
            buttonTitle={t("settings.list.createTopic")}
            createEntitityUID={"TOPIC_NEW"}
            editEntityUID={"TOPIC_EDIT"}
            topics={Topics}
          />
        );
      case "TOPIC_NEW":
        return (
          <SettingsAI
            backTo="TOPIC"
            settingType="topic"
            isEdit={false}
            setDisplayedView={setDisplayedView}
            opt={displayedOpt}
            sectionTitle={t("settings.list.newTopic")}
          />
        );
      case "TOPIC_EDIT":
        return (
          <SettingsAI
            backTo="TOPIC"
            settingType="topic"
            isEdit
            sectionTitle={displayedOpt?.label}
            opt={displayedOpt}
            initialValues={{ ...displayedOpt }}
            setDisplayedView={setDisplayedView}
          />
        );
      case "DICTIONARY":
        return (
          <DictionariesList
            buttonTitle={t("settings.list.createDictionary")}
            createEntitityUID={"DICTIONARY_NEW"}
            editEntityUID={"DICTIONARY_EDIT"}
            dictionaries={state?.aiDictionariesConfig}
          />
        );
      case "JOBS":
        return (
          <JobsList
            buttonTitle={t("settings.list.createJob")}
            createEntitityUID={"DICTIONARY_NEW"}
            editEntityUID={"DICTIONARY_EDIT"}
            dictionaries={state?.aiDictionariesConfig}
          />
        );
      case "TRIGGERS":
        return <TriggersList dictionaries={state?.aiDictionariesConfig} />;
      case "ALERTS":
        return <AlertsList />;
      case "FUNCTIONS":
        return (
          <FunctionsList
            buttonTitle={t("settings.list.createFunction")}
            createEntitityUID={"FUNCTION_NEW"}
            editEntityUID={"FUNCTION_EDIT"}
            dictionaries={state?.agentsFunctions}
          />
        );
      case "DB_CONNECTIONS":
        return (
          <ConnectionsList
            buttonTitle={t("settings.list.createDbConnection")}
            createEntitityUID={"DB_CONNECTION_NEW"}
            editEntityUID={"DB_CONNECTION_EDIT"}
            dictionaries={state?.dbConnections}
            setDisplayedView={setDisplayedView}
          />
        );
      case "AUTHORISATIONS":
        return (
          <AuthorisationsList
            buttonTitle={t("settings.list.createAuthorisation")}
            createEntitityUID={"AUTH_NEW"}
            editEntityUID={"AUTH_EDIT"}
            dictionaries={state?.authorisations}
            setDisplayedView={setDisplayedView}
          />
        );
      case "HOSTINGS":
        return (
          <HostingsList
            buttonTitle={t("settings.list.createProvider")}
            createEntitityUID={"HOST_NEW"}
            editEntityUID={"HOST_EDIT"}
            dictionaries={state?.hostings}
            setDisplayedView={setDisplayedView}
          />
        );
      case "CHANNELS":
        return (
          <ChannelsList
            buttonTitle={t("settings.list.createChannel")}
            createEntitityUID={"CHANNEL_NEW"}
            editEntityUID={"CHANNEL_EDIT"}
            dictionaries={state?.channels}
            setDisplayedView={setDisplayedView}
          />
        );
      case "SITES":
        return (
          <SitesList
            buttonTitle={t("settings.list.createSite")}
            createEntitityUID={"SITE_NEW"}
            editEntityUID={"SITE_EDIT"}
            sites={state?.sites}
          />
        );
      case "BENCHMARKS":
        return (
          <BenchmarksList
            buttonTitle={t("settings.list.createBenchmark")}
            createEntitityUID={"BENCHMARK_NEW"}
            editEntityUID={"BENCHMARK_EDIT"}
            sites={state?.benchmarks}
          />
        );
      case "EXEC_ENVS":
        return (
          <ExecEnvsList
            buttonTitle={t("settings.list.createExecutionHook")}
            createEntitityUID={"EXEC_ENV_NEW"}
            editEntityUID={"EXEC_ENV_EDIT"}
            execEnvs={state?.execEnvs}
          />
        );
      case "GENERATOR_SETTINGS":
        return (
          <GeneratorConfigList
            buttonTitle={""}
            createEntitityUID={"GENERATOR_SETTINGS_NEW"}
            editEntityUID={"GENERATOR_SETTINGS_EDIT"}
          />
        );
      case "ROLES_SETTINGS":
        return (
          <QaConfigList
            buttonTitle={""}
            createEntitityUID={"ROLES_SETTINGS_NEW"}
            editEntityUID={"ROLES_SETTINGS_EDIT"}
          />
        );
      case "TRAINING":
        return (
          <TrainingSettings
            workspace={workspace}
            handleWorkspaceUpdate={handleWorkspaceUpdate}
          />
        );
      case "PARSING":
        return (
          <ParsingSettings handleWorkspaceUpdate={handleWorkspaceUpdate} />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <AppJoyRide
        handleCallback={(e) => {
          const isClose = e?.action === "close" || e?.status === "skipped";
          if (e?.status === "finished") {
            if (!isClose && activeIntro) markSectionIntroSeen(activeIntro.uid);
            setActiveIntro(null);
          } else if (isClose) {
            setActiveIntro(null);
          }
        }}
        run={hasActiveIntro}
        steps={sectionIntroSteps}
        spotlightClicks={true}
      />
      <View
        nativeID={
          displayedView ? `settings_section_${displayedView}` : undefined
        }
        style={{ flex: 1 }}
      >
        {viewRenderer(mapDisplayedViewWithComponent, styles)}
      </View>
    </>
  );
};
const styleGenerator = (currentTheme, isMobileScreen) => {
  return StyleSheet.create({
    container: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginVertical: 10,
      paddingHorizontal: 20,
    },
    icon: {
      borderRadius: 5,
      width: "auto",
      height: "auto",
    },
    modal: {
      borderRadius: 30,
      height: Platform.OS === "web" && !isMobileScreen ? 500 : "95%",
      left: "auto",
      marginHorizontal: Platform.OS === "web" ? "auto" : "5%",
      marginVertical: "auto",
      top: "auto",
      width: Platform.OS === "web" && !isMobileScreen ? 500 : "99%",
    },
    modalInnerContent: {
      paddingHorizontal: 20,
      paddingTop: "auto",
    },
    modalRightIcon: {
      position: "relative",
      top: "auto",
      right: "auto",
    },
    modalWrapper: {
      height: "auto",
    },
    title: {
      fontSize: currentTheme?.theme?.largest_font_size,
      fontWeight: "bold",
    },
  });
};

export default SettingsScreen;
